import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Calculator, Loader2, Save, AlertTriangle, XCircle, Wallet, Send } from "lucide-react";
import { api, apiError, eur } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { DoQuestionario, DoDocumenti } from "@/components/DoQuestionario";

const field = "w-full rounded-lg border border-input bg-white px-3 py-2 text-sm text-forest outline-none focus:border-forest";
const LIV = { basso: "bg-emerald-100 text-emerald-800", medio: "bg-amber-100 text-amber-800", alto: "bg-red-100 text-red-800" };

function DoResult({ res }) {
  const p = res.premium;
  const blocked = res.status === "non_ammissibile";
  return (
    <div className="mt-5 space-y-3" data-testid="do-result">
      {(blocked ? res.eligibility.blocking_errors : res.eligibility.warnings).map((m, i) => (
        <div key={i} className={`flex gap-2 rounded-lg border p-2.5 text-xs ${blocked ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
          {blocked ? <XCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}{m}
        </div>
      ))}
      {res.rischio && <div className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${LIV[res.rischio.livello]}`} data-testid="do-rischio">Indice di rischio {res.rischio.livello}</div>}
      {p && (
        <div className="rounded-lg bg-sage/50 p-4">
          {p.rows.map((r, i) => <div key={i} className="flex justify-between gap-2 py-1 text-xs"><span className="text-muted-foreground">{r.voce}</span><span className="font-medium text-forest">{eur(r.net)}</span></div>)}
          <div className="flex justify-between py-1 text-xs"><span className="text-muted-foreground">Imposte</span><span className="text-forest">{eur(p.tax_total)}</span></div>
          <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2">
            <span className="font-medium text-forest">Premio annuo lordo indicativo</span>
            <span className="font-heading text-2xl font-semibold text-forest" data-testid="do-premio">{eur(p.gross_total)}</span>
          </div>
        </div>
      )}
      {res.provvigione && (
        <div className="flex items-center justify-between rounded-lg border border-terracotta/30 bg-terracotta/5 p-3 text-sm" data-testid="do-provvigione">
          <span className="flex items-center gap-2 text-terracotta"><Wallet className="h-4 w-4" />Provvigione {res.provvigione.pct}%</span>
          <b className="text-terracotta">{eur(res.provvigione.importo)}</b>
        </div>
      )}
    </div>
  );
}

export default function DoQuote() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const editId = params.get("edit");
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const [f, setF] = useState({ tipo_ente: "cooperativa", attivo_patrimoniale: "", massimale_rc: 500000, opzioni: {}, osservazione_mesi: 0,
    tutela_legale: false, massimale_tl: 25000, numero_soci: "", data_effetto: new Date().toISOString().slice(0, 10) });
  const [q, setQ] = useState({ assetti_adeguati: true });
  const [docs, setDocs] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => {
    api.get("/do/config").then(({ data }) => setConfig(data)).catch((e) => toast.error(apiError(e)));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then(({ data: x }) => {
      const i = x.do_input || {};
      setAnagraficaId(x.anagrafica_id || "");
      setF((o) => ({ ...o, ...i, attivo_patrimoniale: i.attivo_patrimoniale ?? "", numero_soci: i.numero_soci ?? "" }));
      setQ(i.questionario || {});
      setDocs(x.do_documenti || []);
      setRes(x.calc_result || null);
    }).catch((e) => toast.error(apiError(e)));
  }, [editId]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const mRc = config.massimali_rc[f.tipo_ente] || [];
  const mTl = config.massimali_tl[f.tipo_ente] || [];
  const body = () => ({ ...f, attivo_patrimoniale: Number(f.attivo_patrimoniale || 0), massimale_rc: Number(f.massimale_rc),
    massimale_tl: Number(f.massimale_tl), numero_soci: f.numero_soci === "" ? null : Number(f.numero_soci), questionario: q });
  const calc = async () => {
    setBusy("calc");
    try { const { data } = await api.post("/do/calculate", body()); setRes(data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona il cliente");
    setBusy("save");
    try {
      const { data } = await api.post("/do/quotes", { ...body(), anagrafica_id: anagraficaId, documenti: docs, quote_id: editId || undefined });
      toast.success(`Pratica ${data.codice_prev || ""} salvata: dal fascicolo inviala all'Agenzia per la Direzione`);
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const onEnte = (v) => set({ tipo_ente: v, massimale_rc: (config.massimali_rc[v] || [500000])[0], massimale_tl: (config.massimali_tl[v] || [25000])[0] });

  return (
    <div className="space-y-6" data-testid="do-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-sector-green">CAT · Terzo Settore</div>
        <h1 className="mt-1 flex items-center gap-2 font-heading text-3xl font-semibold text-forest"><Briefcase className="h-7 w-7" />D&O Directors & Officers</h1>
        <p className="mt-1 text-sm text-muted-foreground">Assimoco Mod. 20E (Ed. 10/2023) · RC di amministratori, sindaci e dirigenti · Tutela Legale degli organi sociali.</p>
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="do-direzione-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Prodotto riservato alla Direzione: il premio è indicativo. La polizza si emette solo dopo l'autorizzazione della Direzione Tecnica, che esamina questionario, statuto e bilanci.
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 lg:col-span-3">
          <Card className="space-y-4 rounded-xl border-border/60 p-5">
            <h2 className="font-heading text-lg font-semibold text-forest">Ente e coperture</h2>
            <label className="block text-xs text-muted-foreground">Cliente
              <select className={field} value={anagraficaId} onChange={(e) => setAnagraficaId(e.target.value)} disabled={!!editId} data-testid="do-anagrafica">
                <option value="">Seleziona il cliente…</option>{anagrafiche.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
              </select></label>
            <label className="block text-xs text-muted-foreground">Tipologia di ente
              <select className={field} value={f.tipo_ente} onChange={(e) => onEnte(e.target.value)} data-testid="do-ente">
                {config.enti.map((e) => <option key={e.code} value={e.code}>{e.label}</option>)}</select></label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Totale attivo patrimoniale (€)</Label>
                <Input type="number" min={0} value={f.attivo_patrimoniale} onChange={(e) => set({ attivo_patrimoniale: e.target.value })} data-testid="do-attivo" placeholder="es. 1500000" /></div>
              <label className="block text-xs text-muted-foreground">Massimale RC
                <select className={field} value={f.massimale_rc} onChange={(e) => set({ massimale_rc: e.target.value })} data-testid="do-massimale-rc">
                  {mRc.map((m) => <option key={m} value={m}>{eur(m)}</option>)}</select></label>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-semibold text-forest">Garanzie aggiuntive a pagamento</div>
              {config.opzioni.map((o) => (
                <label key={o.code} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 text-sm">
                  <Checkbox data-testid={`do-opt-${o.code}`} checked={!!f.opzioni[o.code]} onCheckedChange={(v) => set({ opzioni: { ...f.opzioni, [o.code]: !!v } })} />
                  <span className="text-forest">{o.label} <span className="text-muted-foreground">(+{o.pct}% del premio RC)</span></span>
                </label>
              ))}
              <p className="text-[11px] text-muted-foreground">Sconto tecnico 5% con due garanzie aggiuntive, 10% con tre o più.</p>
              <label className="block text-xs text-muted-foreground">Periodo di osservazione in aggiunta
                <select className={field} value={f.osservazione_mesi} onChange={(e) => set({ osservazione_mesi: Number(e.target.value) })} data-testid="do-osservazione">
                  {config.osservazione.map((m) => <option key={m} value={m}>{m ? `${m} mesi` : "Nessuno"}</option>)}</select></label>
            </div>
            <div className="space-y-3 rounded-lg border border-border/60 p-3">
              <div className="flex items-center justify-between"><Label>Sezione Tutela Legale (con RC)</Label>
                <Switch checked={f.tutela_legale} onCheckedChange={(v) => set({ tutela_legale: v })} data-testid="do-tl" /></div>
              {f.tutela_legale && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs text-muted-foreground">Massimale TL
                    <select className={field} value={f.massimale_tl} onChange={(e) => set({ massimale_tl: e.target.value })} data-testid="do-massimale-tl">
                      {mTl.map((m) => <option key={m} value={m}>{eur(m)}</option>)}</select></label>
                  {f.tipo_ente === "ets" && <div className="space-y-1.5"><Label>Numero soci/assicurati</Label>
                    <Input type="number" min={1} max={1000} value={f.numero_soci} onChange={(e) => set({ numero_soci: e.target.value })} data-testid="do-soci" /></div>}
                </div>
              )}
            </div>
          </Card>
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Questionario</h2>
            <DoQuestionario config={config} ente={f.tipo_ente} q={q} set={setQ} />
          </Card>
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-1 font-heading text-lg font-semibold text-forest">Documenti per la Direzione</h2>
            <p className="mb-3 text-xs text-muted-foreground">Obbligatori per la quotazione: senza questionario firmato e timbrato la Direzione non autorizza l'emissione.</p>
            <DoDocumenti config={config} docs={docs} setDocs={setDocs} ente={f.tipo_ente} />
          </Card>
        </div>
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <Card className="sticky top-24 rounded-xl border-border/60 p-5">
            <div className="space-y-1.5"><Label>Data di effetto</Label>
              <Input type="date" value={f.data_effetto} onChange={(e) => set({ data_effetto: e.target.value })} data-testid="do-data-effetto" /></div>
            <Button onClick={calc} disabled={busy === "calc" || !f.attivo_patrimoniale} data-testid="do-calc" className="mt-4 h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
              {busy === "calc" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Calculator className="h-5 w-5" />}Calcola premio indicativo
            </Button>
            {res && <DoResult res={res} />}
            {res && res.status !== "non_ammissibile" && !(isAdmin && !editId) && (
              <Button onClick={save} disabled={busy === "save" || !anagraficaId} data-testid="do-save" className="mt-4 h-12 w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                {busy === "save" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-4 w-4" />}{editId ? "Aggiorna pratica" : "Salva pratica"}
              </Button>
            )}
            <p className="mt-3 flex gap-2 text-[11px] text-muted-foreground"><Send className="h-3.5 w-3.5 shrink-0" />Dopo il salvataggio, dal fascicolo invii la pratica all'Agenzia, che la trasmette alla Direzione Tecnica Assimoco.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
